import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  AngularFirestore,
  AngularFirestoreCollection,
} from '@angular/fire/compat/firestore';
import { getFunctions, httpsCallable } from '@angular/fire/functions';
import { serverTimestamp } from '@angular/fire/firestore';
import { Observable, map, take } from 'rxjs';
import { Event, EventStatus } from '../models/event.model';
import { MemberType } from '../models/membership.status';
import { UtilsService } from './utils.service';

@Injectable({
  providedIn: 'root',
})
export class EventService {
  http = inject(HttpClient);
  utilsService = inject(UtilsService);

  constructor(private afs: AngularFirestore) {}

  getEventsByGroup(
    groupId: string,
    memberType: MemberType,
    limit?: number,
    lastRecord?: Event,
  ): Observable<Event[]> {
    const eventsCollection: AngularFirestoreCollection<Event> =
      this.afs.collection<Event>(`events`, (ref) => {
        ref.where('groupId', '==', groupId);

        if (memberType != MemberType.admin) {
          ref.where('status', 'in', [
            EventStatus.active,
            EventStatus.cancelled,
          ]);
        } else {
          ref.where('status', 'in', [
            EventStatus.active,
            EventStatus.request,
            EventStatus.rejected,
            EventStatus.cancelled,
          ]);
        }
        ref.orderBy('startDateTime', 'desc');
        return ref;
      });

    return eventsCollection.get().pipe(
      take(1),
      map((changes) => {
        return changes.docs.map((a) => {
          var data: Event = a.data() as Event;
          data.id = a.id;
          return data;
        });
      }),
    );
  }

  getEventById(id: string): Observable<Event> {
    return this.afs
      .collection('events')
      .doc<Event>(id)
      .get()
      .pipe(
        take(1),
        map((changes) => {
          const data = changes.data() as Event;
          data.id = changes.id;
          return data;
        }),
      );
  }

  async create(id: string, event: any): Promise<void> {
    // console.log(`EventService :: create: ${JSON.stringify(event, null, 2)}`);
    return this.afs.collection('events').doc(id).set(event);
  }

  async update(id: string, event: any): Promise<void> {
    // console.log(
    //   `EventService :: update: ${id}: ${JSON.stringify(event, null, 2)}`
    // );
    return this.afs.collection('events').doc(id).update(event);
  }

  async delete(id: string): Promise<void> {
    return this.afs.collection('events').doc(id).delete();
  }

  getAttendeeStatus(eventId: string, userId: string): Observable<any> {
    return this.afs
      .collection('events')
      .doc(eventId)
      .collection('attendees')
      .doc<any>(userId)
      .get()
      .pipe(
        map((doc) => {
          if (doc.exists) {
            return doc.data();
          }
          return undefined;
        })
      );
  }

  async updateAttendeeStatus(
    eventId: string,
    userId: string,
    name: string,
    photoUrl: string | null,
    status: string,
    additionalGuests: number
  ): Promise<void> {
    return this.afs
      .collection('events')
      .doc(eventId)
      .collection('attendees')
      .doc(userId)
      .set({
        status,
        userId,
        name,
        photoUrl,
        additionalGuests,
        modified: serverTimestamp(),
      });
  }

  async getEventStats(groupId: string): Promise<any> {
    try {
      const functions = getFunctions();
      const callable = httpsCallable(functions, 'eventCount', {
        limitedUseAppCheckTokens: true,
      });
      const result = await callable({
        groupId,
      });
      // console.log(`Response: ${JSON.stringify(result)}`);
      return result.data;
    } catch (error) {
      // console.log(`EventService :: getEventStats: Error: ${error}`);
      return Promise.reject('Unable to fetch Event Statistics');
    }
  }

  getVolunteeringDetails(groupId: string, eventId: string): Observable<any[]> {
    return this.afs
      .collection('groups')
      .doc(groupId)
      .collection('events_calendar', (ref) => ref.where('refId', '==', eventId))
      .valueChanges({ idField: 'id' });
  }

  async volunteerForEvent(
    groupId: string,
    calendarEventId: string,
    user: { uid: string; name: string; photoUrl: string | null },
    metadata: {
      eventId: string;
      eventName: string;
      eventDescription: string;
      eventImage: string | null;
      subEventName: string;
      subEventDate: any; // Timestamp or date
      volunteersRequired: number;
      volunteerCriteria: string;
    }
  ): Promise<void> {
    const docRef = this.afs
      .collection('groups')
      .doc(groupId)
      .collection('events_calendar')
      .doc(calendarEventId).ref;

    return this.afs.firestore.runTransaction(async (transaction) => {
      const docSnap = await transaction.get(docRef);
      
      let volunteers = [];
      let required = metadata.volunteersRequired;
      let existingData: any = {};

      if (docSnap.exists) {
        existingData = docSnap.data() as any;
        volunteers = existingData.volunteers || [];
        required = existingData.volunteersRequired || 0;
      } else {
        const dateVal = metadata.subEventDate;
        const d = dateVal.toDate ? dateVal.toDate() : new Date(dateVal);
        
        existingData = {
          groupId: groupId,
          type: 'event',
          name: metadata.subEventName || metadata.eventName,
          description: metadata.eventDescription,
          date: dateVal,
          eventDate: dateVal,
          month: d.getMonth() + 1,
          year: d.getFullYear(),
          day: d.getDate(),
          refId: metadata.eventId,
          imageUrl: metadata.eventImage,
          volunteersRequired: metadata.volunteersRequired,
          volunteerCriteria: metadata.volunteerCriteria,
          volunteers: [],
        };
      }

      // Check if user already volunteered
      const alreadyVolunteered = volunteers.some((v: any) => v.uid === user.uid);
      if (alreadyVolunteered) {
        throw new Error('You have already volunteered for this session.');
      }

      // Check vacancies
      if (volunteers.length >= required) {
        throw new Error('No vacancies available.');
      }

      const newVolunteer = {
        uid: user.uid,
        name: user.name,
        photoUrl: user.photoUrl,
        date: new Date(),
      };
      const updatedVolunteers = [...volunteers, newVolunteer];

      const prefix = `event_${metadata.eventId}_`;
      const subEventId = calendarEventId.substring(prefix.length);

      const eventRef = this.afs.collection('events').doc(metadata.eventId).ref;
      const eventSnap = await transaction.get(eventRef);
      const eventData = eventSnap.exists ? eventSnap.data() as any : {};
      const subEvents = eventData.events || {};
      const subEvent = subEvents[subEventId] || {};
      const currentCount = subEvent.volunteersCount || 0;

      transaction.set(docRef, {
        ...existingData,
        volunteers: updatedVolunteers,
      }, { merge: true });

      transaction.update(eventRef, {
        [`events.${subEventId}.volunteersCount`]: currentCount + 1,
      });
    });
  }

  async unvolunteerForEvent(
    groupId: string,
    calendarEventId: string,
    userId: string,
    eventId: string
  ): Promise<void> {
    const docRef = this.afs
      .collection('groups')
      .doc(groupId)
      .collection('events_calendar')
      .doc(calendarEventId).ref;

    return this.afs.firestore.runTransaction(async (transaction) => {
      const docSnap = await transaction.get(docRef);
      if (!docSnap.exists) {
        throw new Error('Event session not found.');
      }
      const data = docSnap.data() as any;
      const volunteers = data.volunteers || [];
      
      const isVolunteered = volunteers.some((v: any) => v.uid === userId);
      if (!isVolunteered) {
        return; // No-op if not volunteered
      }

      const updatedVolunteers = volunteers.filter((v: any) => v.uid !== userId);

      const prefix = `event_${eventId}_`;
      const subEventId = calendarEventId.substring(prefix.length);

      const eventRef = this.afs.collection('events').doc(eventId).ref;
      const eventSnap = await transaction.get(eventRef);
      const eventData = eventSnap.exists ? eventSnap.data() as any : {};
      const subEvents = eventData.events || {};
      const subEvent = subEvents[subEventId] || {};
      const currentCount = subEvent.volunteersCount || 0;

      transaction.update(docRef, {
        volunteers: updatedVolunteers,
      });

      transaction.update(eventRef, {
        [`events.${subEventId}.volunteersCount`]: Math.max(0, currentCount - 1),
      });
    });
  }

  updateSubEvents(eventId: string, events: any): Promise<void> {
    return this.afs.collection('events').doc(eventId).update({ events });
  }
}
